import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIcon } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDatepicker, MatDatepickerModule } from '@angular/material/datepicker';
import { MatRadioModule } from '@angular/material/radio';
import { MatSliderModule } from '@angular/material/slider';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatOptionModule } from '@angular/material/core';
import { AsYouType, CountryCode } from 'libphonenumber-js';
import moment from 'moment';
import { Logger } from '../../../../../infrastructure/logging/logger';
import { UserContextProvider } from '../../../../../infrastructure/context/user-context-provider';
import { US_COUNTRY_ID } from '../../../../../infrastructure/context/UserContext';
import { Country } from '../../../../../shared/address/Country';
import { State } from '../../../../../shared/address/State';
import { getCountryCodeFromCountryId } from '../../../../../utilities/CountryUtility';
import { formatCurrencyString } from '../../../../../utilities/CurrencyUtility';
import { Reservation, ReservationSelectedPolicy } from '../../../../reservations/models/Reservation';
import { getClaimNegligenceDescription } from '../../../ClaimHelper';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { getDatePickerDateFormat } from '../../../../../utilities/DateUtility';
import { formatPhoneNumber } from '../../../../../utilities/PhoneUtility';

@Component({
  selector: 'app-what-happened-step',
  templateUrl: './what-happened-step.component.html',
  styleUrls: ['./what-happened-step.component.scss'],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'en' },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
    { provide: MAT_DATE_FORMATS, useFactory: getDatePickerDateFormat, deps: [UserContextProvider] },
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS] }
  ],
  standalone: true,
  imports: [
    CommonModule,
    MatExpansionModule,
    MatIcon,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatDatepicker,
    MatOptionModule,
    MatRadioModule,
    MatSliderModule,
    MatInputModule,
    MatCheckboxModule,
    MatSelectModule
  ]
})
export class WhatHappenedStepComponent implements OnInit {

  @Output() isTheftOrFireChecked: EventEmitter<any> = new EventEmitter();
  @Output() isInjuryChecked: EventEmitter<any> = new EventEmitter();
  @Input() reservation!: Reservation;
  @Input('whatHappenedForm') form!: FormGroup;
  @Input('lossForm') lossForm!: FormGroup;
  @Input('injuryAddressForm') injuryForm!: FormGroup;
  @Input('governmentDepartmentForm') governmentForm!: FormGroup;
  @Input('countries') countries!: Country[];
  @Input('states') states!: State[];

  title: string = "What Happened?";
  complete: boolean = false;
  error: boolean = false;
  panelOpen: boolean = false;
  icon: string = "expand_more";
  lowestDeductible!: number;
  minDate!: Date;
  maxDate!: Date;

  getClaimNegligenceDescription = getClaimNegligenceDescription;
  formatCurrencyString = formatCurrencyString;
  defaultCountryId = US_COUNTRY_ID;

  constructor(
    public userContextProvider: UserContextProvider,
    public dateAdapter: DateAdapter<Date>,
    private logService: Logger
  ) { }

  ngOnInit() {
    this.setupDamageDateRange();
    this.setExistingDamageDateValue();
    this.subscribeToInjuryCountryCodeChange();
  }

  subscribeToInjuryCountryCodeChange() {
    this.injuryForm.get('injury_address.country_id')?.valueChanges.subscribe(_ => {
      this.updatePhoneNumber(this.injuryForm.get('injury_phone')?.value);
    });
  }

  setupDamageDateRange() {
    try {
      if (this.reservation.id > 0) {
        this.minDate = moment.utc(this.reservation.arrival_date).toDate();
      } else {
        this.minDate = new Date(2019, 12, 31);
      }

      const departureDate = moment.utc(this.reservation.departure_date).toDate();
      const now = moment.utc().toDate();
      this.maxDate = now > departureDate ? departureDate : now;
    } catch (e) {
      this.logService.handleError(`Unable to convert Reservation Arrival Date: ${this.reservation.arrival_date} or Departure Date: ${this.reservation.departure_date} into a date`, `Unable to convert Reservation Arrival or Departure Date`, `Unable to convert Reservation Arrival Date: ${this.reservation.arrival_date} or Departure Date: ${this.reservation.departure_date} into a date`);
    }
  }

  setExistingDamageDateValue() {
    if (this.form.get('damage_date')?.value === undefined || this.form.get('damage_date')?.value === '') {
      const now = new Date().toISOString();
      const departureDate = new Date(this.reservation.departure_date).toISOString();
      this.form.get('damage_date')?.setValue(now > departureDate ? departureDate : now);
    }
  }

  onTheftOrFireCheckboxChange(value: any) {
    if (!value.checked) {
      this.form.removeControl('goverment_department');
    } else {
      this.form.addControl('goverment_department', this.governmentForm);
    }
  }

  onInjuryCheckboxChange(value: any) {
    if (!value.checked) {
      this.form.removeControl('injury_address');
    } else {
      this.form.addControl('injury_address', this.injuryForm);
    }
  }

  onInjuryPhoneNumberChange(event: Event) {
    this.updatePhoneNumber((event.target as HTMLInputElement).value);
  }

  updatePhoneNumber(unformatted: string) {
    const countryId = this.injuryForm.get('injury_address.country_id')?.value || US_COUNTRY_ID;
    if (!countryId || !unformatted) {
      return;
    }

    const countryCode = getCountryCodeFromCountryId(this.countries, countryId);
    if(!countryCode) {
      this.logService.handleError(`Unable to find Country code for Country ID: ${countryId}`, `Unable to find Country code`, `Unable to find Country code for Country ID: ${countryId}`);
      return;
    }

    const ayt = new AsYouType(countryCode as CountryCode);
    this.injuryForm.patchValue({
      injury_phone: ayt.input(unformatted)
    });
  }

  getLowestDeductibleFromPolicies(policies: ReservationSelectedPolicy[]): number {
    let lowestDeductible: number | undefined;
    policies.forEach(policy => {
      if (!lowestDeductible || policy.deductible < lowestDeductible) {
        lowestDeductible = policy.deductible;
      }
    });

    return lowestDeductible ?? 0;
  }

  shouldShowStateInput(): boolean {
    return this.injuryForm.get('injury_address.country_id')?.value == this.defaultCountryId;
  }

  onPhoneNumberChanged(event: Event) {
    this.updateOfficerPhoneNumber((event.target as HTMLInputElement).value);
  }

  updateOfficerPhoneNumber(phoneNumber: string) {
    const formattedPhoneNumber = formatPhoneNumber(phoneNumber, "US");
    this.governmentForm.patchValue({ officer_phone: formattedPhoneNumber });
  }
}